export const SIGNIN_PATH = '/signin';

/**
 * ログイン後の戻り先にしないパス(前方一致)。
 * 認証系ページに戻すとログイン直後にまたログイン画面へ、という堂々巡りになるため除外する。
 */
const EXCLUDED_PATH_PREFIXES = [SIGNIN_PATH, '/register', '/password/reset', '/invitation'];

/**
 * URLパーサーが除去・変換してしまう文字を含むか。
 *
 * タブ・改行はURL解析前に除去されるため '/[TAB]/evil.example' が '//evil.example' に化け、
 * バックスラッシュは '/' として扱われるため '/\evil.example' が同様に化ける。
 * 素の文字列比較では素通りしてしまうので、URLパーサーに渡す前に弾く。
 */
const hasUnsafeCharacter = (value: string): boolean =>
  Array.from(value).some((char) => {
    const codePoint = char.charCodeAt(0);
    // C0制御文字(タブ・改行を含む)とDEL、およびバックスラッシュ
    return codePoint <= 0x1f || codePoint === 0x7f || char === '\\';
  });

/**
 * 戻り先として安全なパスだけを通す。
 *
 * 外部サイトへのオープンリダイレクトを防ぐため、同一オリジンに解決されるパスのみ許可する。
 * react-router の navigate() はクロスオリジンの pushState に失敗すると
 * window.location.assign() へフォールバックするため、ここを通すと実際に外部サイトへ遷移する。
 */
export const sanitizeReturnTo = (value: string | null | undefined): string | null => {
  if (!value?.startsWith('/')) return null;
  if (hasUnsafeCharacter(value)) return null;

  // 文字列比較では判定しきれないため、ブラウザと同じ解釈に正規化してから判定する
  let url: URL;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return null;
  }
  // '//evil.example' 等のスキーム相対URLはここで別オリジンに解決される
  if (url.origin !== window.location.origin) return null;

  const isExcluded = EXCLUDED_PATH_PREFIXES.some(
    (prefix) => url.pathname === prefix || url.pathname.startsWith(`${prefix}/`),
  );
  return isExcluded ? null : `${url.pathname}${url.search}${url.hash}`;
};

/**
 * 戻り先を含むサインインURLを組み立てる。戻せない場合は素の /signin を返す。
 *
 * 401でのハードリダイレクト(window.location.href)を跨いでも戻り先を保持したいため、
 * react-router の state ではなくクエリで持ち回る。
 */
export const buildSigninPath = (returnTo: string | null | undefined): string => {
  const safeReturnTo = sanitizeReturnTo(returnTo);
  return safeReturnTo ? `${SIGNIN_PATH}?next=${encodeURIComponent(safeReturnTo)}` : SIGNIN_PATH;
};
